import { useNavigate } from "react-router-dom";
import { useApp } from "../state";
import { Avatar } from "../components/Avatar";
import { PageHeader } from "../components/PageHeader";
import { formatPtDate, ptDateKey } from "../util";

export function Leaderboard() {
  const { state } = useApp();
  const nav = useNavigate();

  // Keep the table header about the next slate, not prediction deadlines.
  const nextMatch = [...state.matches]
    .filter((m) => m.status === "scheduled")
    .sort((a, b) => a.kickoff_utc.localeCompare(b.kickoff_utc))[0];
  const nextMatchday = nextMatch ? ptDateKey(new Date(nextMatch.kickoff_utc)) : null;
  const kickoffCount = nextMatchday
    ? state.matches.filter((m) => m.status === "scheduled" && ptDateKey(new Date(m.kickoff_utc)) === nextMatchday).length
    : 0;
  const headerSummary = nextMatch
    ? `${kickoffCount} kickoff${kickoffCount === 1 ? "" : "s"} on ${formatPtDate(nextMatch.kickoff_utc)} (PT)`
    : undefined;

  return (
    <div className="page">
      <PageHeader
        title="The Big Table"
        tone="gold"
        sub={headerSummary}
      />

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {state.leaderboard.map((e) => {
          const isTop = e.rank === 1;
          const podiumTone = e.rank === 1
            ? "lb-row--gold"
            : e.rank === 2
              ? "lb-row--silver"
              : e.rank === 3
                ? "lb-row--bronze"
                : "";
          return (
            <div
              key={e.player_id}
              className={`card lb-row ${podiumTone} ${isTop ? "lb-row--top" : ""}`}
              onClick={() => nav(`/profile/${e.player_id}`)}
              style={{ cursor: "pointer" }}
            >
              <RankMark rank={e.rank} />
              <DeltaChip delta={e.delta} />
              <Avatar name={e.name} initials={e.initials} color={e.color} />
              <span className="lb-row__name">{e.name}</span>
              {e.points_gained > 0 && (
                <span className="lb-row__gain lb-row__gain--positive" title="Points gained in the last completed match">
                  +{e.points_gained}
                </span>
              )}
              <span className="lb-row__total">{e.total}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DeltaChip({ delta }: { delta: number }) {
  if (delta > 0) return <span className="chip chip--up" title={`Up ${delta} place${delta === 1 ? "" : "s"}`}>↑{delta}</span>;
  if (delta < 0) return <span className="chip chip--down" title={`Down ${Math.abs(delta)} place${delta === -1 ? "" : "s"}`}>↓{Math.abs(delta)}</span>;
  return <span className="chip chip--flat" title="No rank change">—</span>;
}

function RankMark({ rank }: { rank: number }) {
  const medals: Record<number, { icon: string; label: string }> = {
    1: { icon: "🥇", label: "Gold medal" },
    2: { icon: "🥈", label: "Silver medal" },
    3: { icon: "🥉", label: "Bronze medal" },
  };
  const medal = medals[rank];
  if (medal) return <span className="lb-row__medal" aria-label={medal.label}>{medal.icon}</span>;
  return <span className="lb-row__rank">{rank}</span>;
}
